import React from 'react';
import { Card, Form, Select, Input, Row, Col, Space, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
import FormSection from '../FormSection';

import CurvDown from '../svg/CurvDown';

const { Option } = Select;

interface CoworkersInformationProps {
  form: FormInstance;
  caseType?: string;
  isViewMode?: boolean;
}

const CoworkersInformation: React.FC<CoworkersInformationProps> = ({ form, caseType, isViewMode }) => {
  const isView = isViewMode || new URLSearchParams(window.location.search).get('view') === 'true';
  const yesNoOptions = ['Yes', 'No'];
  const discriminationBasisOptions = [
    'Age', 'Color', 'Mental/Physical Handicap', 'National Origin',
    'Race', 'Religion', 'Reprisal', 'Sex'
  ];

  const sameSupervisorWatch = Form.useWatch(['coworkers_info', 'coworkers_same_supervisor'], form);
  const disableDifferentSupervisor = sameSupervisorWatch !== 'No';

  const otherTreatedSameWatch = Form.useWatch(['coworkers_info', 'other_employees_treated_same'], form);
  const disableCoworkerFields = otherTreatedSameWatch !== 'Yes';

  const isSexualHarassmentOrTermination = caseType === 'Sexual Harassment' || caseType === 'Termination';

  function add(): void {
    throw new Error('Function not implemented.');
  }

  return (
    <div className="flex flex-col w-full h-full">
      <FormSection title="Coworker's Information" columns={2} gapY="gap-y-1 md:gap-y-2" className="coworker-section ">
        <style dangerouslySetInnerHTML={{
          __html: `
          .coworker-section .ant-form-item-label {
            min-height: auto;
            display: flex;
            align-items: flex-start;
            line-height: 1.2;
         }
          @media (min-width: 768px) {
            .coworker-section .ant-form-item-label {
              min-height: 50px;
              display: flex;
              align-items: flex-end;
              margin-bottom: 12px !important;
            }
          }
          @media (max-width: 767px) {   
            .coworker-section .ant-form-item-label > label {
              margin-bottom: 11px !important;
            }
          }
          /* Tighter vertical gap for this section on mobile only */
          .coworker-section .grid {
            gap-y: 12px !important;
          }
          @media (min-width: 768px) {
            .coworker-section .grid {
              gap-y: 37px !important;
            }
          }
          .bases-field .ant-form-item-explain-error {
            margin-top: 28px !important;
          }
        `}} />
        <Form.Item
          label="Were other employees treated the same way as you?"
          name={['coworkers_info', 'other_employees_treated_same']}
          className='md:w-[64%]'
        >
          <Select
            placeholder="Select"
            suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
            onChange={(val) => {
              if (val !== 'Yes') {
                form.setFieldValue(['coworkers_info', 'coworkers'], []);
              }
            }}
          >
            {yesNoOptions.map(option => (
              <Option key={option} value={option}>{option}</Option>
            ))}
          </Select>
        </Form.Item>

        <div className="col-span-1 md:col-span-2">
          <Form.List name={['coworkers_info', 'coworkers']}>
            {(fields, { add, remove }) => (
              <div className="flex flex-col w-full lg:w-[70%] gap-y-[28px] md:gap-y-[30px]">
                <div className="flex items-center justify-between w-full">
                  <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] leading-[29px] m-0">
                    Coworker's Information
                  </h3>
                  <Button
                    type="text"
                    onClick={() => add()}
                    disabled={fields.length >= 3 || isViewMode}
                    className="flex items-center gap-2 text-[#4682B4] hover:text-[#3b6d96] font-['Lexend',sans-serif] p-0 disabled:opacity-50"
                  >
                    <span className="text-[18px] leading-none">+</span> Add More
                  </Button>
                </div>

                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="flex flex-row items-start gap-[8px] md:gap-x-[18px] w-full md:mb-4">
                    {/* Number column */}
                    <div className="w-6 md:w-8 flex justify-center pt-[40px] md:pt-[85px]">
                      <span className="font-['Lexend',sans-serif] text-[12px] md:text-[18px] text-[#1F2A37A1] font-medium">
                        {String(name + 1).padStart(2, '0')}
                      </span>
                    </div>

                    {/* Inputs Container */}
                    <div className="flex-1 grid grid-cols-2 gap-[20px] md:gap-[60px]">
                      {/* Name Input */}
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        label="Names"
                        className="mb-0"
                        rules={[{ required: true, message: 'Names is required!' }]}
                      >
                        <Input placeholder="Names" />
                      </Form.Item>

                      {/* Basis Select */}
                      <Form.Item
                        {...restField}
                        name={[name, 'basis']}
                        label="Basis"
                        className="mb-0 bases-field"
                        rules={[{ required: true, message: 'Basis is required!' }]}>
                        <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                          {discriminationBasisOptions.map(option => (
                            <Option key={option} value={option}>{option}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>

                    {/* Delete Action */}
                    <div className="w-8 md:w-10 flex justify-center items-start pt-[40px] md:pt-[85px]">
                      <button
                        onClick={() => {
                          if (!isView) {
                            remove(name);
                          }
                        }}
                        disabled={isView}
                        type="button"
                        style={isView ? { pointerEvents: 'none', opacity: 0.3, cursor: 'not-allowed' } : undefined}
                        className="p-1 text-[#6FB6F2] hover:text-red-500 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                      >
                        <svg width="20" height="22" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-[18px] w-[12px] md:h-[20px] h-[13px] stroke-current">
                          <path d="M1 4h14M2 4v11a2 2 0 002 2h8a2 2 0 002-2V4M5 4V2a1 1 0 011-1h4a1 1 0 011 1v2M6.5 8v5M9.5 8v5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Form.List>
        </div>

        <div className="col-span-1 md:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[60px] gap-y-[28px] md:gap-y-[37px] lg:w-[65%]">
            {isSexualHarassmentOrTermination && (
              <>
                <Form.Item
                  label="Did your coworkers have the same or very similar job?"
                  name={['coworkers_info', 'coworkers_same_job']}
                >
                  <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                    {yesNoOptions.map(option => (
                      <Option key={option} value={option}>{option}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Were your coworkers the same basis as you?"
                  name={['coworkers_info', 'coworkers_same_basis']}
                >
                  <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                    {yesNoOptions.map(option => (
                      <Option key={option} value={option}>{option}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              label="Did your coworker work for the same supervisor?"
              name={['coworkers_info', 'coworkers_same_supervisor']}
            >
              <Select
                placeholder="Select"
                suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                onChange={(val) => {
                  if (val !== 'No') {
                    form.setFieldValue(['coworkers_info', 'different_supervisor_name'], undefined);
                  }
                }}
              >
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Different Supervisor's name"
              name={['coworkers_info', 'different_supervisor_name']}
              layout="vertical"
              className={`transition-opacity duration-300 ${disableDifferentSupervisor ? 'opacity-50' : 'opacity-100'}`}
            >
              <Input placeholder="Enter here" disabled={disableDifferentSupervisor} />
            </Form.Item>
          </div>
        </div>
      </FormSection>
    </div>
  );
};

export default CoworkersInformation;

